import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import {
  BadgeComponent,
  BadgeColor,
  BadgeType,
  ButtonVariant,
  ButtonComponent,
  Icon,
  ButtonIconOnlyDirective,
  Size,
} from '@circlek-global/lds-components';

import { GtmCustomEventNameEnum } from '~components/gtm-script/gtm-custom-event-name.enum';
import { GtmCustomEventService } from '~components/gtm-script/gtm-custom-event.service';
import type { Station } from '~models/station.model';
import { TranslatePipe } from '~pipes/translate.pipe';
import { StationStatusService } from '~services/station-status.service';
import { StationsService } from '~services/station.service';
import { openDirections } from '~utils/directions.utils';
import { getStationName } from '~utils/station-name';

@Component({
  selector: 'app-station-card',
  imports: [BadgeComponent, ButtonComponent, ButtonIconOnlyDirective, TranslatePipe],
  templateUrl: './station-card.component.html',
  providers: [StationStatusService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StationCardComponent {
  private readonly gtmCustomEventService = inject(GtmCustomEventService);
  private readonly stationsService = inject(StationsService);
  private readonly stationStatusService = inject(StationStatusService);
  readonly directionIcon = Icon.CORNER_UP_RIGHT;
  readonly infoIcon = Icon.INFO_CIRCLE;
  readonly secondaryVariant = ButtonVariant.SECONDARY;
  readonly BadgeColor = BadgeColor;
  readonly BadgeType = BadgeType;
  readonly Size = Size;
  readonly station = input.required<Station>();
  readonly displayName = computed(() => getStationName(this.station()));
  readonly fullAddress = computed(() => {
    const addr = this.station().address;

    return `${addr.street}, ${addr.city}, ${addr.postalCode}`;
  });
  readonly visibleServices = computed(() => this.station().services.slice(0, 4));
  readonly overflowCount = computed(() => {
    const total = this.station().services.length;

    return total > 4 ? total - 4 : 0;
  });
  readonly statusBadgeColor = this.stationStatusService.statusBadgeColor;
  readonly statusLabel = this.stationStatusService.statusLabel;

  constructor() {
    effect(() => {
      this.stationStatusService.openingHours.set(this.station().openingHours);
    });
  }

  onDirectionsClick(): void {
    const stationData = this.station();

    this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.GetDirection);

    openDirections({ latitude: stationData.latitude, longitude: stationData.longitude });
  }

  openDetails(): void {
    this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.Go2StoreInfo, { action: 'info_icon' });

    this.stationsService.selectStation(this.station());
  }
}
